/*
 * cáene — get the full ritual (PDP-RITUAL/TASK.md, 2026-09-12).
 *
 * Presentationen är serverrenderad i sections/caene-full-ritual.liquid. Det
 * här skriptet gör tre saker och inget mer: håller summan i takt med
 * variantbytet på sidan, lägger trion i varukorgen, och rapporterar utfallet.
 *
 * Köpmekaniken är ärvd oförändrad från den verifierade routine-widgeten
 * (/tmp/caene-ritual-source/assets/caene-routine-widget.js, spike 2026-07-21):
 * trion säljs som Recharge-bundle-produkten 'bygg-ditt-eget-3-pack'.
 *   1. Bygg 132-byte selection-blob (big-endian):
 *      [u32 0][u64 bundleVariant][u32 count] + per rad:
 *      [u64 collectionId][u64 productId][u64 variantId][u64 qty][u32 0]
 *      + [u32 unixTs][u32 0]
 *   2. POST { bundle: base64(blob) } till bundling-proxyn (kräver Accept:
 *      application/json och X-Recharge-App, annars svarar den HTML)
 *      → { code, id, message }, där id är _rb_id.
 *   3. POST till butikens lokaliserade cart/add.js med bundle-varianten och
 *      properties { Contents, _rb_id, _trio_name }.
 *
 * Skillnader mot originalet, medvetna:
 *   - ingen dataLayer-push och ingen IntersectionObserver: sektionen lägger
 *     inte till någon spårning,
 *   - inget prenumerationsläge: intervallet går inte att läsa ur allokeringen,
 *     så det valet ligger kvar hos PDP:ns egna prenumerationskontroller,
 *   - ingen drawer-pollning. Efter ett lyckat tillägg ber vi Rebuy hämta om
 *     sin egen kopia av varukorgen och öppnar Smart Cart, som är den yta där
 *     appens gåvo- och progressbeteende faktiskt finns. Saknas Rebuy, eller
 *     är Smart Cart inte klar, går vi precis som förut till varukorgens
 *     lokaliserade URL som sektionen skickar med i data-cart-url.
 *   - inga per-rad-priser räknas fram här. Alla belopp kommer som ören från
 *     Liquid; skriptet formaterar dem, det hittar inte på dem.
 */

const CAENE_RITUAL_COLLECTION_ID = '686214644092';
const CAENE_RITUAL_BUNDLE_ENDPOINT = '/tools/recurring/bundling-storefront-manager/api/v1/bundles';
const CAENE_RITUAL_NAVIGATION_GRACE_MS = 4000;
/* Rebuys återanrop kan utebli eller komma sent. Tidsgränsen är den enda vägen
   vidare i så fall och får inte vara så kort att ett långsamt men fungerande
   anrop kapas; den är satt att stämmas av i browsertestet. */
const CAENE_RITUAL_SMART_CART_TIMEOUT_MS = 3000;

class CaeneFullRitual extends HTMLElement {
  connectedCallback() {
    if (!this.readPayload() || !this.readTargets()) return;

    this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.savingLabel = this.dataset.savingLabel || '';
    if (!this.defaultCtaLabel) this.defaultCtaLabel = this.ctaLabelEl.textContent.trim();

    this.onCtaClick = () => this.addRitual();
    this.cta.addEventListener('click', this.onCtaClick);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
    }

    if (this.completed) {
      this.showCompleted();
    } else {
      this.updateTotals();
      this.updateAvailability();
    }
  }

  disconnectedCallback() {
    this.cta?.removeEventListener('click', this.onCtaClick);
    this.unsubscribe?.();
    this.unsubscribe = null;
    clearTimeout(this.navigationTimer);
    clearTimeout(this.smartCartTimer);
    this.smartCartTimer = null;
    /* Ett återanrop som kommer efter att elementet lämnat sidan får varken
       öppna Smart Cart eller navigera. Samma spärr som avgör utgången i
       settle() stänger därför dörren här. */
    this.settled = true;
  }

  readPayload() {
    const node = this.querySelector('script[data-ritual-json]');
    if (!node) return false;
    try {
      const payload = JSON.parse(node.textContent);
      this.bundle = payload.bundle;
      this.items = payload.items.map((item) => ({ ...item }));
    } catch (error) {
      console.error('[caene-ritual] Could not parse the ritual payload', error);
      return false;
    }
    this.pageItem = this.items.find((item) => item.isPageProduct);
    return Boolean(this.bundle && this.bundle.variantId && this.items.length === 3);
  }

  readTargets() {
    this.cta = this.querySelector('[data-ritual-cta]');
    this.ctaLabelEl = this.querySelector('[data-ritual-cta-label]');
    this.priceEl = this.querySelector('[data-ritual-price]');
    this.strikeEl = this.querySelector('[data-ritual-strike]');
    this.savingEl = this.querySelector('[data-ritual-saving]');
    this.savingAmountEl = this.querySelector('[data-ritual-saving-amount]');
    this.messageEl = this.querySelector('[data-ritual-message]');
    this.messageTextEl = this.querySelector('[data-ritual-message-text]');
    this.messageLinkEl = this.querySelector('[data-ritual-message-link]');
    this.errorTemplate = this.querySelector('template[data-ritual-error]');
    this.addedTemplate = this.querySelector('template[data-ritual-added]');

    const ready =
      this.cta &&
      this.ctaLabelEl &&
      this.priceEl &&
      this.strikeEl &&
      this.savingEl &&
      this.savingAmountEl &&
      this.messageEl &&
      this.messageTextEl &&
      this.messageLinkEl;

    if (!ready) console.error('[caene-ritual] Missing markup targets — the section stays inert');
    return Boolean(ready);
  }

  /* Ören, inte avrundade kronor. Butikens penningformat kapar decimalerna, så
     Liquid-strängarna i markeringen är bara reserven för den som kör utan JS;
     så snart elementet ansluter skrivs alla tre talen om med Intl, som följer
     valutans egen decimalregel och därför visar exakt det som debiteras. */
  money(cents) {
    return new Intl.NumberFormat(this.dataset.currency === 'SEK' ? 'sv-SE' : this.dataset.locale, {
      style: 'currency', currency: this.dataset.currency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  fullSum() {
    return this.items.reduce((sum, item) => sum + item.price, 0);
  }

  updateTotals() {
    this.priceEl.textContent = this.money(this.bundle.price);

    const full = this.fullSum();
    const saving = full - this.bundle.price;
    if (saving > 0) {
      this.strikeEl.textContent = this.money(full);
      this.strikeEl.hidden = false;
      this.savingAmountEl.textContent = this.money(saving);
      this.savingEl.hidden = true;
    } else {
      this.strikeEl.hidden = true;
      this.savingEl.hidden = true;
    }
  }

  /* Dawn publicerar variantbytet för hela sidan. Bekräfta att bytet kommer
     från sidans egen produkt innan något av det används. */
  onVariantChange(event) {
    const data = event?.data;
    if (!data || !data.variant || !this.pageItem) return;

    const owner = document.querySelector(`product-info[data-section="${data.sectionId}"]`);
    if (!owner || owner.dataset.productId !== String(this.pageItem.productId)) return;

    this.pageItem.variantId = data.variant.id;
    this.pageItem.price = data.variant.price;
    this.pageItem.available = Boolean(data.variant.available);

    this.updateTotals();
    this.updateAvailability();
  }

  updateAvailability() {
    if (this.completed || this.pending) return;
    const soldOut = this.items.some((item) => !item.available);
    const soldOutLabel = this.dataset.soldOutLabel;
    this.cta.disabled = soldOut;
    if (soldOut && soldOutLabel) {
      this.ctaLabelEl.textContent = soldOutLabel;
    } else {
      this.ctaLabelEl.textContent = this.defaultCtaLabel;
    }
  }

  setMessage(text, withLink) {
    this.messageTextEl.textContent = text;
    this.messageLinkEl.hidden = !withLink;
    this.messageEl.hidden = false;
  }

  clearMessage() {
    this.messageEl.hidden = true;
    this.messageLinkEl.hidden = true;
    this.messageTextEl.textContent = '';
  }

  showCompleted() {
    this.cta.disabled = true;
    this.cta.dataset.loading = 'false';
    this.cta.removeAttribute('aria-busy');
    this.ctaLabelEl.textContent = this.defaultCtaLabel;
    this.setMessage(this.addedTemplate?.content.textContent.trim() || '', true);
  }

  /* Recharge selection-blob, byte för byte som i originalet. */
  buildSelectionBlob(items) {
    const buffer = new ArrayBuffer(16 + items.length * 36 + 8);
    const view = new DataView(buffer);
    let offset = 0;
    view.setUint32(offset, 0);
    offset += 4;
    view.setBigUint64(offset, BigInt(this.bundle.variantId));
    offset += 8;
    view.setUint32(offset, items.length);
    offset += 4;
    for (const item of items) {
      view.setBigUint64(offset, BigInt(CAENE_RITUAL_COLLECTION_ID));
      offset += 8;
      view.setBigUint64(offset, BigInt(item.productId));
      offset += 8;
      view.setBigUint64(offset, BigInt(item.variantId));
      offset += 8;
      view.setBigUint64(offset, 1n);
      offset += 8;
      view.setUint32(offset, 0);
      offset += 4;
    }
    view.setUint32(offset, Math.floor(Date.now() / 1000));
    offset += 4;
    view.setUint32(offset, 0);
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
  }

  async addRitual() {
    if (this.pending || this.completed) return;

    /* Läget fryses innan begäran går iväg. Byter kunden variant medan anropet
       är i luften läggs ändå exakt det som stod på knappen i varukorgen. */
    const snapshot = this.items.map((item) => ({ ...item }));
    if (snapshot.some((item) => !item.available)) {
      this.updateAvailability();
      return;
    }

    this.pending = true;
    this.clearMessage();
    this.cta.disabled = true;
    this.cta.dataset.loading = 'true';
    this.cta.setAttribute('aria-busy', 'true');
    if (this.dataset.addingLabel) this.ctaLabelEl.textContent = this.dataset.addingLabel;

    let added = false;

    try {
      const registration = await fetch(CAENE_RITUAL_BUNDLE_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Recharge-App': 'storefront-client',
        },
        body: JSON.stringify({ bundle: this.buildSelectionBlob(snapshot) }),
      });
      const registered = await registration.json().catch(() => null);
      if (!registration.ok || !registered || !registered.id) {
        throw new Error(`bundle registration failed (${registration.status})`);
      }

      const properties = {
        Contents: snapshot.map((item) => `1x ${item.title.toLowerCase()}`).join('\n'),
        _rb_id: registered.id,
      };
      if (this.bundle.setName) properties._trio_name = this.bundle.setName;

      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: String(this.bundle.variantId), quantity: 1, properties }],
        }),
      });

      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        throw new Error(failure.description || `HTTP ${response.status}`);
      }

      added = true;
    } catch (error) {
      console.error('[caene-ritual] Could not add the ritual', error);
      this.setMessage(this.errorTemplate?.content.textContent.trim() || '', false);
      this.pending = false;
      this.cta.dataset.loading = 'false';
      this.cta.removeAttribute('aria-busy');
      /* Knappen öppnas av lagerkontrollen, aldrig rakt av: blev något slut
         medan anropet var i luften ska den förbli stängd och säga varför. */
      this.updateAvailability();
      return;
    }

    /* Härifrån ligger trion i varukorgen. Allt som kan gå fel nedanför är
       navigering, aldrig själva tillägget — knappen får därför inte bli en
       försök-igen-knapp, för då lägger kunden till trion två gånger. */
    if (added) {
      this.completed = true;
      this.pending = false;
      this.settled = false;
      this.openSmartCart();
    }
  }

  /* Rebuys egna metoder, enligt developers.rebuyengine.com: Cart.fetchShopifyCart
     tar ett återanrop och uppdaterar appens kopia av varukorgen, SmartCart.show
     öppnar lådan, och SmartCart.status === 'ready' är klarsignalen. Är något av
     det inte på plats är native-varukorgen den degraderade men fungerande
     utgången — gåvan är då inte garanterad, för den ytan saknar appens
     gåvo- och progressbeteende. */
  openSmartCart() {
    const rebuyCart = window.Rebuy?.Cart;
    const smartCart = window.Rebuy?.SmartCart;
    const ready =
      smartCart?.status === 'ready' &&
      typeof smartCart.show === 'function' &&
      typeof rebuyCart?.fetchShopifyCart === 'function';

    if (!ready) {
      this.settle(false);
      return;
    }

    this.smartCartTimer = setTimeout(() => this.settle(false), CAENE_RITUAL_SMART_CART_TIMEOUT_MS);

    try {
      const refresh = rebuyCart.fetchShopifyCart(() => this.settle(true));
      /* Metoden är dokumenterad med återanrop, men returnerar i vissa versioner
         en thenable. Ett avslag där får inte bli en obehandlad rejection — och
         en uppfyllelse är inte klarsignalen, den kommer via återanropet. */
      if (refresh && typeof refresh.then === 'function') {
        refresh.then(undefined, (error) => {
          console.error('[caene-ritual] Rebuy could not refresh the cart', error);
          this.settle(false);
        });
      }
    } catch (error) {
      console.error('[caene-ritual] Rebuy could not refresh the cart', error);
      this.settle(false);
    }
  }

  /* Exakt en utgång räknas. Tidsgränsen och återanropet tävlar, och den som
     kommer först stänger den andra ute — annars kan kunden både få lådan
     öppnad och sidan bortnavigerad under sig. */
  settle(openSmartCart) {
    if (this.settled) return;
    this.settled = true;
    clearTimeout(this.smartCartTimer);
    this.smartCartTimer = null;

    if (openSmartCart) {
      try {
        window.Rebuy.SmartCart.show();
        this.showCompleted();
        return;
      } catch (error) {
        console.error('[caene-ritual] Smart Cart could not open', error);
      }
    }

    this.navigateToCart();
  }

  navigateToCart() {
    this.navigationTimer = setTimeout(() => this.showCompleted(), CAENE_RITUAL_NAVIGATION_GRACE_MS);
    try {
      window.location.assign(this.cartUrl);
    } catch (error) {
      console.error('[caene-ritual] Could not open the cart', error);
      clearTimeout(this.navigationTimer);
      this.showCompleted();
    }
  }
}

if (!customElements.get('caene-full-ritual')) {
  customElements.define('caene-full-ritual', CaeneFullRitual);
}
